/*
  recipes-search.js (v2) - VanillaGoods recipe search on Typesense.

  Works with the existing Recipes search section: it replaces the section's old filter boxes with
  the ones configured in FACETS below, so no Liquid changes are needed.

  - Within one filter, ticking several options means "any of these" (Muffins OR Scones), and the
    counts beside each option stay honest while you tick (disjunctive facets via multi_search).
  - Diet & allergies is the exception: ticking Vegan + Gluten-Free means both.
  - Filters, search text, sort and page live in the URL, so filtered views can be shared or linked
    (e.g. /pages/recipes?dish=Muffins&diets=Vegan) and the back button works.
  - Needs the index built by sync_recipes_typesense.py (fields dish, flavor, diets, form, ...).

  To change which filters show, or their order, edit FACETS.
*/
(function () {
  'use strict';

  // field: Typesense field. label: heading. match: 'any' (OR) or 'all' (AND). open: expanded at load.
  // The three 'browse' facets render as one block: Food/Drinks tabs, the category list, then the dishes
  // inside the ticked categories (the dish counts come back already narrowed by the category filter).
  var FACETS = [
    { field: 'kind', label: 'Food or drink', type: 'tabs', match: 'any', group: 'browse', order: ['Food', 'Drinks'] },
    { field: 'browse', label: 'Category', match: 'any', group: 'browse', open: true, show: 99 },
    { field: 'dish', label: 'Dish', match: 'any', group: 'browse', open: true, show: 8 },
    { field: 'diets', label: 'Diet & allergies', match: 'all', open: true,
      order: ['Vegan', 'Gluten-Free', 'Dairy-Free', 'Egg-Free', 'No Tree Nuts'] },
    { field: 'time', label: 'Hands-on time', type: 'select', open: true },
    { field: 'ready', label: 'Ready in', type: 'select', open: true },
    { field: 'flavor', label: 'Flavor', match: 'any', open: true, show: 8 },
    { field: 'form', label: 'Vanilla form', match: 'any', open: false,
      order: ['Beans', 'Extract', 'Paste', 'Powder', 'Vanilla Sugar'] },
    { field: 'equipment', label: 'Equipment', match: 'any', open: false },
    { field: 'level', label: 'Difficulty', match: 'any', open: false, order: ['Easy', 'Medium', 'Advanced'] },
    { field: 'occasions', label: 'Occasion', match: 'any', open: false, show: 8 },
    { field: 'best_vanilla', label: 'Best vanilla', match: 'any', open: false },
    { field: 'season', label: 'Season', match: 'any', open: false, order: ['Fall', 'Holiday', 'Summer', 'Year-round'] }
  ];
  // the seasonal quick-pick tile, by month (0 = January)
  var SEASONS = [
    { months: [8, 9, 10], field: 'season', value: 'Fall', label: 'Fall favorites' },
    { months: [11], field: 'season', value: 'Holiday', label: 'Holiday baking' },
    { months: [0, 1], field: 'occasions', value: 'Date Night', label: 'Date-night desserts' },
    { months: [2, 3, 4], field: 'occasions', value: 'Spring', label: 'Spring favorites' },
    { months: [5, 6, 7], field: 'season', value: 'Summer', label: 'Summer treats' }
  ];
  var SELECTS = {
    time: { param: 'time', options: [['', 'Any'], ['15', '15 min or less'], ['30', '30 min or less'], ['45', '45 min or less'], ['60', '1 hour or less']],
      filter: function (v) { return v ? 'active_minutes:<=' + Number(v) : ''; } },
    ready: { param: 'ready', options: [['', 'Any time'], ['60', 'Within 1 hour'], ['240', 'Within 4 hours'], ['ahead', 'Make ahead (chill, freeze or steep)']],
      filter: function (v) { return v === 'ahead' ? 'make_ahead:=true' : (v ? 'total_minutes:<=' + Number(v) : ''); } }
  };
  // "Highest rated" comes back once recipes have ratings: ['rating:desc,rating_count:desc', 'Highest rated']
  var SORTS = [['', 'Best match'], ['published_at:desc', 'Newest'], ['active_minutes:asc', 'Quickest to make'],
    ['title:asc', 'A to Z']];
  // with no search and no filters, open on this month's baking: in-season food first, then in-season drinks
  function seasonalSort() {
    var m = new Date().getMonth(), s = SEASONS.filter(function (x) { return x.months.indexOf(m) > -1; })[0];
    if (!s) return undefined;
    var c = s.field + ':' + tick(s.value);
    return '_eval([(' + c + ' && kind:Food):3,(' + c + '):2,(kind:Food):1]):desc,published_at:desc';
  }
  // the drink categories (names as the index has them), so the dish list can say "drinks" instead of "dishes"
  var DRINK_CATS = /^(Milkshakes|Smoothies|Coffee|Tea, Chai|Hot Chocolate|Syrups|Lemonades|Frapp|Eggnog|International)/;
  // flavor values that were merged in the index on 2026-10-14 (sync_recipes_typesense.py FLAVOR_MERGES): old links map to the new name
  var FLAVOR_ALIASES = { 'Vanilla': 'Classic', 'Vanilla Bean': 'Classic', 'Double-Fold': 'Classic', 'Alcohol-Free': 'Classic',
    'Dark Chocolate': 'Chocolate', 'Cocoa': 'Chocolate', 'Au Chocolat': 'Chocolate',
    'Coffee': 'Coffee & Espresso', 'Espresso': 'Coffee & Espresso', 'Cold Brew': 'Coffee & Espresso',
    'Cinnamon Roll': 'Cinnamon', 'Snickerdoodle': 'Cinnamon', 'Peach Cobbler': 'Peach', 'Blueberry Muffin': 'Blueberry',
    'Banana Bread': 'Banana', 'Peanut': 'Peanut Butter', 'Honey Lavender': 'Lavender', 'Lavender & Rose': 'Lavender',
    'Matcha': 'Matcha & Tea', 'Tea & Matcha': 'Matcha & Tea', 'Earl Grey': 'Matcha & Tea', 'Rooibos': 'Matcha & Tea',
    'Jasmine': 'Matcha & Tea', 'Thai Tea': 'Matcha & Tea', 'Hojicha': 'Matcha & Tea', 'Dulce De Leche': 'Caramel',
    'Apple Cider': 'Apple', 'Pumpkin Spice': 'Pumpkin', 'Cookie': 'Cookies & Cream', 'Sugar Cookie': 'Cookies & Cream',
    'Cookie Dough': 'Cookies & Cream', 'Nut': 'Mixed Nuts', 'Cashew': 'Mixed Nuts', 'Walnut': 'Mixed Nuts', 'Macadamia': 'Mixed Nuts',
    'Toasted Marshmallow': 'Marshmallow', 'Fig': 'Fig & Date', 'Carrot': 'Carrot Cake', 'Key Lime Pie': 'Key Lime' };
  var FACET_FIELDS = FACETS.filter(function (f) { return f.type !== 'select'; }).map(function (f) { return f.field; });
  var EXCLUDE = 'steps_text,ingredients_text,body_text,tags';

  // ------------------------------------------------------------------ helpers
  function esc(v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function tick(v) { return '`' + String(v).replace(/`/g, '') + '`'; }
  // GA4: one event per filter change, so the next round of sidebar decisions can use real clicks
  function track(field, value, on) {
    try {
      var p = { filter_name: field, filter_value: String(value), filter_action: on ? 'add' : 'remove' };
      if (typeof window.gtag === 'function') window.gtag('event', 'filter_select', p);
      else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'filter_select' }, p));
    } catch (e) { /* analytics must never break the search */ }
  }
  function minutes(m) {
    m = Number(m || 0);
    if (!m) return '';
    if (m >= 2880) { var d = Math.round(m / 1440); return (d % 7 === 0 ? (d / 7) + ' week' + (d / 7 === 1 ? '' : 's') : d + ' days'); }
    if (m >= 60) { var h = Math.floor(m / 60), r = m % 60; return h + ' hr' + (r ? ' ' + r + ' min' : ''); }
    return m + ' min';
  }
  function endpoint(cfg, path) {
    var p = (cfg.path || '').trim(); if (p && p.charAt(0) !== '/') p = '/' + p; p = p.replace(/\/$/, '');
    return (cfg.protocol || 'https') + '://' + cfg.host + (cfg.port ? ':' + cfg.port : '') + p + path;
  }

  // ------------------------------------------------------------------ state <-> URL
  function readUrl(state) {
    var u = new URLSearchParams(window.location.search);
    state.query = u.get('q') || '';
    state.sortBy = u.get('sort') || '';
    state.page = Math.max(1, parseInt(u.get('page'), 10) || 1);
    state.selects = { time: u.get('time') || '', ready: u.get('ready') || '' };
    state.selected = {};
    FACET_FIELDS.forEach(function (f) {
      var vals = u.getAll(f);
      if (f === 'flavor') vals = vals.map(function (v) { return FLAVOR_ALIASES[v] || v; });   // old shared links keep working
      if (vals.length) state.selected[f] = new Set(vals);
    });
  }
  function writeUrl(state) {
    var u = new URLSearchParams();
    if (state.query) u.set('q', state.query);
    FACET_FIELDS.forEach(function (f) { (state.selected[f] ? Array.from(state.selected[f]) : []).forEach(function (v) { u.append(f, v); }); });
    Object.keys(SELECTS).forEach(function (k) { if (state.selects[k]) u.set(SELECTS[k].param, state.selects[k]); });
    if (state.sortBy) u.set('sort', state.sortBy);
    if (state.page > 1) u.set('page', state.page);
    var qs = u.toString(), url = window.location.pathname + (qs ? '?' + qs : '');
    if (url !== window.location.pathname + window.location.search) window.history.replaceState(null, '', url);
  }

  // ------------------------------------------------------------------ filters
  function facetClause(state, field) {
    var sel = state.selected[field]; if (!sel || !sel.size) return '';
    var def = FACETS.filter(function (f) { return f.field === field; })[0] || {};
    var vals = Array.from(sel).map(tick);
    if (def.match === 'all') return vals.map(function (v) { return field + ':=' + v; }).join(' && ');
    return field + ':=[' + vals.join(',') + ']';
  }
  function filterBy(state, skipField) {
    var parts = [];
    FACET_FIELDS.forEach(function (f) { if (f !== skipField) { var c = facetClause(state, f); if (c) parts.push(c); } });
    Object.keys(SELECTS).forEach(function (k) { var c = SELECTS[k].filter(state.selects[k]); if (c) parts.push(c); });
    return parts.join(' && ');
  }
  function selectedCount(state) {
    var n = 0;
    FACET_FIELDS.forEach(function (f) { n += state.selected[f] ? state.selected[f].size : 0; });
    Object.keys(SELECTS).forEach(function (k) { if (state.selects[k]) n += 1; });
    return n;
  }

  // ------------------------------------------------------------------ search
  function queryFields(cfg) {
    var base = (cfg.queryBy || 'title,summary,ingredients_text,steps_text').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    ['dish', 'flavor', 'diets'].forEach(function (f) { if (base.indexOf(f) < 0) base.push(f); });
    var weight = { title: 8, dish: 6, flavor: 5, diets: 4, tags: 3, summary: 2 };
    return { by: base.join(','), weights: base.map(function (f) { return weight[f] || 1; }).join(',') };
  }
  async function runSearch(root) {
    var st = root._vg, cfg = st.cfg, qf = queryFields(cfg), my = ++st.seq;
    setStatus(root, 'loading');
    var common = { collection: cfg.collection, q: st.query.trim() || '*', query_by: qf.by, query_by_weights: qf.weights,
      max_facet_values: 100, prioritize_exact_match: true };
    var main = Object.assign({}, common, { filter_by: filterBy(st), facet_by: FACET_FIELDS.join(','), per_page: st.perPage,
      page: st.page, exclude_fields: EXCLUDE,
      sort_by: st.sortBy || (!st.query.trim() && !selectedCount(st) ? seasonalSort() : undefined) });
    var searches = [main];
    // disjunctive counts: for each "any" facet with a selection, count as if that facet were unselected
    var extra = FACETS.filter(function (f) { return f.match === 'any' && st.selected[f.field] && st.selected[f.field].size; });
    extra.forEach(function (f) { searches.push(Object.assign({}, common, { filter_by: filterBy(st, f.field), facet_by: f.field, per_page: 0 })); });
    searches.forEach(function (s) { Object.keys(s).forEach(function (k) { if (s[k] === undefined || s[k] === '') delete s[k]; }); });
    try {
      var res = await fetch(endpoint(cfg, '/multi_search'), { method: 'POST',
        headers: { 'X-TYPESENSE-API-KEY': cfg.key, 'Content-Type': 'application/json' },
        body: JSON.stringify({ searches: searches }) });
      if (!res.ok) throw new Error('Typesense ' + res.status);
      var payload = await res.json();
      if (my !== st.seq) return;                                   // a newer search already started
      var results = payload.results || [];
      if (results[0] && results[0].error) throw new Error(results[0].error);
      var facets = {};
      (results[0].facet_counts || []).forEach(function (fc) { facets[fc.field_name] = fc.counts; });
      extra.forEach(function (f, i) {
        var r = results[i + 1]; if (r && r.facet_counts && r.facet_counts[0]) facets[f.field] = r.facet_counts[0].counts;
      });
      renderFacets(root, facets);
      renderChips(root);
      renderCards(root, results[0].hits || []);
      renderMeta(root, results[0]);
      var empty = root.querySelector('[data-recipes-empty]');
      if (empty) {
        if (!empty.hasAttribute('data-default')) empty.setAttribute('data-default', empty.textContent);
        empty.textContent = selectedCount(st) ? 'No recipes match all of these filters. Remove one of the filters above to see more.'
          : (st.query ? 'No recipes match \u201c' + st.query + '\u201d. Try a simpler word, like a flavor or dish.' : empty.getAttribute('data-default'));
      }
      setStatus(root, (results[0].hits || []).length ? 'ok' : 'empty');
      writeUrl(st);
    } catch (e) {
      if (my !== st.seq) return;
      // the index doesn't have a newer filter field yet (sync_recipes_typesense.py not rerun): drop it and retry
      var missing = /facet field named `([^`]+)`/.exec(String(e && e.message || ''));
      if (missing && FACET_FIELDS.indexOf(missing[1]) >= 0) {
        var gone = missing[1];
        FACETS = FACETS.filter(function (f) { return f.field !== gone; });
        FACET_FIELDS = FACET_FIELDS.filter(function (f) { return f !== gone; });
        delete st.selected[gone];
        var box = root.querySelector('[data-vgrs-facet="' + gone + '"]'), det = box && (box.closest('[data-vgrs-wrap]') || box.closest('details'));
        if (det) det.remove();
        return runSearch(root);
      }
      setStatus(root, 'error');
      var el = root.querySelector('[data-recipes-error]'); if (el) el.textContent = 'Search is unavailable right now. Please try again in a moment.';
      if (window.console) console.error(e);
    }
  }
  function go(root, resetPage) { if (resetPage) root._vg.page = 1; runSearch(root); }

  // ------------------------------------------------------------------ rendering
  function setStatus(root, s) {
    ['loading', 'error', 'empty'].forEach(function (k) { var el = root.querySelector('[data-recipes-' + k + ']'); if (el) el.hidden = s !== k; });
    root.classList.toggle('is-loading', s === 'loading');
  }
  function buildPanel(root) {
    var aside = root.querySelector('.recipes-search__filters'); if (!aside) return;
    aside.querySelectorAll('.recipes-search__facet').forEach(function (n) { n.remove(); });   // old boxes
    var st = root._vg, sid = root.dataset.sectionId || 'x';
    var head = document.createElement('div');
    head.className = 'vgrs__panel-head';
    head.innerHTML = '<div class="vgrs__chips" data-vgrs-chips hidden></div>';
    aside.appendChild(head);
    var browseDone = false;
    FACETS.forEach(function (f) {
      if (f.group === 'browse') {
        if (browseDone) return;
        browseDone = true;
        var b = document.createElement('details');
        b.className = 'recipes-search__facet vgrs__facet vgrs__browse';
        b.open = true;
        b.innerHTML = '<summary>Browse</summary>' +
          '<div class="vgrs__tabs" role="group" aria-label="Food or drink" data-vgrs-tabs></div>' +
          '<div data-vgrs-wrap="browse"><p class="vgrs__sub-k">Category</p><div data-vgrs-facet="browse"><p class="recipes-search__facet-empty">Loading...</p></div></div>' +
          '<div class="vgrs__sub" data-vgrs-wrap="dish"><p class="vgrs__sub-k" data-vgrs-sub-label>Popular dishes</p><div data-vgrs-facet="dish"><p class="recipes-search__facet-empty">Loading...</p></div></div>';
        aside.appendChild(b);
        return;
      }
      var d = document.createElement('details');
      d.className = 'recipes-search__facet vgrs__facet';
      if (f.open || (st.selected[f.field] && st.selected[f.field].size) || (f.type === 'select' && st.selects[f.field])) d.open = true;
      if (f.type === 'select') {
        var id = 'vgrs-' + f.field + '-' + sid;
        d.innerHTML = '<summary>' + esc(f.label) + '</summary><div class="vgrs__select"><label class="vgrs__sr" for="' + id + '">' + esc(f.label) +
          '</label><select id="' + id + '" data-vgrs-select="' + f.field + '">' + SELECTS[f.field].options.map(function (o) {
            return '<option value="' + esc(o[0]) + '"' + (st.selects[f.field] === o[0] ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
          }).join('') + '</select></div>';
      } else {
        d.innerHTML = '<summary>' + esc(f.label) + (f.match === 'all' ? ' <span class="vgrs__hint">(all selected)</span>' : '') +
          '</summary><div data-vgrs-facet="' + f.field + '"><p class="recipes-search__facet-empty">Loading…</p></div>';
      }
      aside.appendChild(d);
    });
    // mobile: collapse filters behind a button
    var toggle = root.querySelector('[data-vgrs-toggle]');
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button'; toggle.className = 'vgrs__toggle'; toggle.setAttribute('aria-expanded', 'false');
      toggle.innerHTML = 'Filters <span data-vgrs-count></span>';
      aside.parentNode.insertBefore(toggle, aside);
    }
    toggle.addEventListener('click', function () {
      var open = !root.classList.contains('vgrs--filters-open');
      root.classList.toggle('vgrs--filters-open', open); toggle.setAttribute('aria-expanded', String(open));
    });
  }
  function renderTabs(root, f, counts) {
    var st = root._vg, tabs = root.querySelector('[data-vgrs-tabs]'); if (!tabs) return;
    var n = {}; counts.forEach(function (c) { n[String(c.value)] = Number(c.count || 0); });
    var cur = st.selected[f.field] && st.selected[f.field].size ? Array.from(st.selected[f.field])[0] : '';
    tabs.innerHTML = [''].concat(f.order).map(function (v) {
      return '<button type="button" class="vgrs__tab" data-vgrs-tab="' + esc(v) + '" aria-pressed="' + (cur === v ? 'true' : 'false') + '">' + esc(v || 'All') +
        (v && n[v] ? ' <span class="vgrs__tab-n">' + n[v].toLocaleString() + '</span>' : '') + '</button>';
    }).join('');
  }
  function renderFacets(root, facets) {
    var st = root._vg;
    FACETS.forEach(function (f) {
      if (f.type === 'select') return;
      if (f.type === 'tabs') { renderTabs(root, f, facets[f.field] || []); return; }
      var box = root.querySelector('[data-vgrs-facet="' + f.field + '"]'); if (!box) return;
      var sel = st.selected[f.field] || new Set(), counts = new Map();
      (facets[f.field] || []).forEach(function (c) { counts.set(String(c.value), Number(c.count || 0)); });
      sel.forEach(function (v) { if (!counts.has(v)) counts.set(v, 0); });
      var items = Array.from(counts.entries()).filter(function (e) { return e[1] > 0 || sel.has(e[0]); });
      // a fixed order where one exists (diets, difficulty, season); everything else by recipe count, biggest first
      if (f.order) items.sort(function (a, b) { var ia = f.order.indexOf(a[0]), ib = f.order.indexOf(b[0]); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || b[1] - a[1]; });
      else items.sort(function (a, b) { return b[1] - a[1] || a[0].localeCompare(b[0]); });
      var details = box.closest('[data-vgrs-wrap]') || box.closest('details');
      if (!items.length) { if (details) details.hidden = true; return; }
      if (details) details.hidden = false;
      if (f.field === 'dish') {
        // "dishes" for food, "drinks" when the Drinks tab or only drink categories are ticked
        var lab = root.querySelector('[data-vgrs-sub-label]'), bs = st.selected.browse, kk = st.selected.kind;
        var cats = bs ? Array.from(bs) : [];
        var drinky = cats.length ? cats.every(function (c) { return DRINK_CATS.test(c); }) : !!(kk && kk.has('Drinks'));
        var noun = drinky ? 'drinks' : 'dishes', cap = drinky ? 'Drinks' : 'Dishes';
        if (lab) lab.textContent = cats.length === 1 ? cap + ' in ' + cats[0] : (cats.length > 1 ? cap + ' in these categories' : 'Popular ' + noun);
      }
      var limit = f.show || 99, expanded = box.getAttribute('data-expanded') === '1';
      var html = items.map(function (e, i) {
        var hidden = !expanded && i >= limit && !sel.has(e[0]);
        return '<label class="recipes-search__facet-item vgrs__item"' + (hidden ? ' hidden' : '') + '><input type="checkbox" data-vgrs-input data-field="' +
          esc(f.field) + '" value="' + esc(e[0]) + '"' + (sel.has(e[0]) ? ' checked' : '') + '><span class="vgrs__label">' + esc(e[0]) +
          '</span><span class="recipes-search__facet-count">' + e[1].toLocaleString() + '</span></label>';
      }).join('');
      if (items.length > limit) html += '<button type="button" class="vgrs__more" data-vgrs-more="' + f.field + '">' + (expanded ? 'Show fewer' : 'Show all ' + items.length) + '</button>';
      box.innerHTML = '<div class="recipes-search__facet-items">' + html + '</div>';
    });
    var n = selectedCount(st), c = root.querySelector('[data-vgrs-count]'); if (c) c.textContent = n ? '(' + n + ')' : '';
  }
  function renderChips(root) {
    var st = root._vg, box = root.querySelector('[data-vgrs-chips]'); if (!box) return;
    var chips = [];
    FACET_FIELDS.forEach(function (f) { (st.selected[f] ? Array.from(st.selected[f]) : []).forEach(function (v) { chips.push({ f: f, v: v, label: v }); }); });
    Object.keys(SELECTS).forEach(function (k) {
      var v = st.selects[k]; if (!v) return;
      var o = SELECTS[k].options.filter(function (x) { return x[0] === v; })[0];
      chips.push({ f: k, v: v, label: o ? o[1] : v, select: true });
    });
    box.hidden = !chips.length;
    box.innerHTML = chips.map(function (c) {
      return '<button type="button" class="vgrs__chip" data-vgrs-remove="' + esc(c.f) + '" data-value="' + esc(c.v) + '"' + (c.select ? ' data-select="1"' : '') +
        ' aria-label="Remove filter ' + esc(c.label) + '">' + esc(c.label) + ' <span aria-hidden="true">&times;</span></button>';
    }).join('') + (chips.length > 1 ? '<button type="button" class="vgrs__clear" data-vgrs-clear>Clear all</button>' : '');
  }
  function stars(r) {
    var pct = Math.max(0, Math.min(100, Number(r) / 5 * 100));
    return '<span class="vgrs__stars" style="--pct:' + pct + '%" aria-hidden="true"></span>';
  }
  /* My Kitchen heart: assets/vg-me.js handles the click and keeps it in step with the heart on the recipe page. */
  function vgHeart(id, title) {
    var m = String(id == null ? '' : id).match(/(\d+)$/);
    if (!m) return '';
    var t = String(title || '').replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
    return '<button type="button" class="vg-save-icon" data-vg-save-recipe="' + m[1] + '" data-title="' + t + '" aria-pressed="false" aria-label="Save ' + t + '">' +
      '<svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/></svg></button>';
  }
  function renderCards(root, hits) {
    var el = root.querySelector('[data-recipes-results]'); if (!el) return;
    el.innerHTML = hits.map(function (h) {
      var d = h.document || {}, title = String(d.title || 'Recipe').split(' | ')[0], meta = [];
      if (d.active_minutes) meta.push('<span class="recipes-search__chip">' + esc(minutes(d.active_minutes)) + ' hands-on</span>');
      if (d.make_ahead && d.total_minutes) meta.push('<span class="recipes-search__chip vgrs__chip-soft">Ready in ' + esc(minutes(d.total_minutes)) + '</span>');
      (d.diets || []).filter(function (x) { return x !== 'No Tree Nuts'; }).slice(0, 2).forEach(function (x) { meta.push('<span class="recipes-search__chip vgrs__chip-diet">' + esc(x) + '</span>'); });
      var img = d.image_url ? '<div class="recipes-search__card-image-wrap"><img class="recipes-search__card-image" src="' + esc(d.image_url) +
        (d.image_url.indexOf('?') < 0 ? '?width=600' : '&width=600') + '" alt="' + esc(d.image_alt || title) + '" loading="lazy" width="600" height="600"></div>' : '';
      var rating = d.rating_count ? '<div class="vgrs__rating">' + stars(d.rating) + '<span>' + Number(d.rating).toFixed(1) + ' (' + d.rating_count + ')</span></div>' : '';
      var kicker = [d.dish, d.flavor && d.flavor !== 'Classic' ? d.flavor : ''].filter(Boolean).join(' · ');
      return '<article class="recipes-search__card"><a class="recipes-search__card-link" href="' + esc(d.url || '#') + '">' + img +
        '<div class="recipes-search__card-body">' + (kicker ? '<p class="vgrs__kicker">' + esc(kicker) + '</p>' : '') +
        '<h3 class="recipes-search__card-title">' + esc(title) + '</h3>' + rating +
        (meta.length ? '<div class="recipes-search__card-meta">' + meta.join('') + '</div>' : '') + '</div></a>' + vgHeart(d.id, title) + '</article>';
    }).join('');
  }
  function renderMeta(root, r) {
    var st = root._vg, found = Number(r.found || 0), pages = Math.max(1, Math.ceil(found / st.perPage));
    st.totalPages = pages; st.page = Math.min(st.page, pages);
    var s = root.querySelector('[data-recipes-summary]'); if (s) s.textContent = found.toLocaleString() + ' recipe' + (found === 1 ? '' : 's');
    var pag = root.querySelector('[data-recipes-pagination]'); if (pag) pag.hidden = pages <= 1;
    var lab = root.querySelector('[data-recipes-page-label]'); if (lab) lab.textContent = 'Page ' + st.page + ' of ' + pages;
    var prev = root.querySelector('[data-recipes-prev]'); if (prev) prev.disabled = st.page <= 1;
    var next = root.querySelector('[data-recipes-next]'); if (next) next.disabled = st.page >= pages;
  }

  // ------------------------------------------------------------------ title jump (unchanged behaviour)
  function buildTitleJump(root) {
    var aside = root.querySelector('.recipes-search__filters'); if (!aside) return;
    var st = root._vg, wrap = document.createElement('div'), id = 'vg-recipejump-' + (root.dataset.sectionId || 'x');
    wrap.className = 'recipes-search__titlejump';
    wrap.innerHTML = '<label class="recipes-search__titlejump-label" for="' + id + '">Find a recipe</label><div class="recipes-search__titlejump-box"><input type="text" id="' +
      id + '" class="recipes-search__titlejump-input" placeholder="Start typing a recipe name…" autocomplete="off"><ul class="recipes-search__titlejump-list" hidden></ul></div>';
    aside.insertBefore(wrap, aside.firstChild);
    var input = wrap.querySelector('input'), list = wrap.querySelector('ul');
    input.addEventListener('input', debounce(async function () {
      var q = input.value.trim(); if (q.length < 2) { list.hidden = true; list.innerHTML = ''; return; }
      try {
        var r = await fetch(endpoint(st.cfg, '/collections/' + encodeURIComponent(st.cfg.collection) + '/documents/search?' +
          new URLSearchParams({ q: q, query_by: 'title', per_page: '8', include_fields: 'title,url' })), { headers: { 'X-TYPESENSE-API-KEY': st.cfg.key } });
        var p = await r.json(), hits = p.hits || [];
        list.innerHTML = hits.length ? hits.map(function (h) { return '<li><a href="' + esc(h.document.url) + '">' + esc(String(h.document.title).split(' | ')[0]) + '</a></li>'; }).join('')
          : '<li class="recipes-search__titlejump-empty">No matching recipes</li>';
        list.hidden = false;
      } catch (e) { list.hidden = true; }
    }, 200));
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) list.hidden = true; });
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') list.hidden = true; });
  }

  // ------------------------------------------------------------------ styles for the new pieces
  function injectCss() {
    if (document.getElementById('vgrs-css')) return;
    var s = document.createElement('style'); s.id = 'vgrs-css';
    s.textContent = [
      '.vgrs__item{display:flex;align-items:center;gap:8px;padding:4px 0;cursor:pointer}',
      '.vgrs__item[hidden],.vgrs__facet[hidden],[data-vgrs-wrap][hidden]{display:none!important}',
      '.vgrs__item input:checked+.vgrs__label{font-weight:600}',
      '.vgrs__tabs{display:flex;gap:6px;margin:6px 0 4px}',
      '.vgrs__tab{flex:1;min-height:36px;padding:6px 8px;border:1px solid #d6c4ad;border-radius:8px;background:#fffdf9;color:#5c4033;font:inherit;font-size:13px;cursor:pointer}',
      '.vgrs__tab[aria-pressed="true"]{border-color:#5c4033;background:rgba(92,64,51,.1);font-weight:600}',
      '.vgrs__tab-n{font-size:11px;opacity:.7;margin-left:2px}',
      '.vgrs__sub-k{margin:10px 0 2px;font-size:11.5px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:#9a7b5f}',
      '.vgrs__sub{margin:4px 0 0 14px;padding-left:10px;border-left:2px solid #e6dccd}',
      '.vgrs__item input{accent-color:#5c4033;margin:0}.vgrs__label{flex:1}',
      '.vgrs__hint{font-weight:400;font-size:11px;opacity:.65}',
      '.vgrs__more{background:none;border:0;padding:4px 0;color:#5c4033;text-decoration:underline;text-underline-offset:2px;cursor:pointer;font:inherit;font-size:12.5px}',
      '.vgrs__select select{width:100%;padding:6px 8px;border:1px solid #d6c4ad;border-radius:6px;background:transparent;font:inherit}',
      '.vgrs__sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}',
      '.vgrs__chips{display:flex;flex-wrap:wrap;gap:6px;margin:4px 0 10px}',
      '.vgrs__chip{border:1px solid #d6c4ad;background:#fffdf9;color:#5c4033;border-radius:999px;padding:4px 10px;font:inherit;font-size:12px;cursor:pointer}',
      '.vgrs__chip:hover{border-color:#5c4033}',
      '.vgrs__clear{background:none;border:0;color:#5c4033;text-decoration:underline;font:inherit;font-size:12px;cursor:pointer}',
      '.vgrs__kicker{margin:0 0 4px;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#9a7b5f}',
      '.vgrs__rating{display:flex;align-items:center;gap:6px;font-size:12px;margin:2px 0 6px;color:#5c4033}',
      '.vgrs__stars{--pct:0%;position:relative;display:inline-block;font-size:13px;line-height:1;letter-spacing:1px}',
      '.vgrs__stars::before{content:"★★★★★";color:#e6dccd}',
      '.vgrs__stars::after{content:"★★★★★";color:#c9962e;position:absolute;left:0;top:0;width:var(--pct);overflow:hidden;white-space:nowrap}',
      '.vgrs__chip-diet{background:rgba(92,64,51,.08)}.vgrs__chip-soft{background:rgba(201,150,46,.12)}',
      '.vgrs__toggle{display:none}',
      '.recipes-search__root.is-loading [data-recipes-results]{opacity:.55;transition:opacity .15s}',
      '@media (max-width:749px){.vgrs__toggle{display:inline-flex;align-items:center;gap:4px;margin:0 0 10px;padding:8px 16px;border:1px solid #5c4033;border-radius:999px;background:#fffdf9;color:#5c4033;font:inherit;font-weight:600;cursor:pointer}',
      '.recipes-search__root:not(.vgrs--filters-open) .recipes-search__filters{display:none}}'
    ].join('\n');
    document.head.appendChild(s);
  }

  // ------------------------------------------------------------------ init
  function init(root) {
    var d = root.dataset;
    if (!d.typesenseHost || !d.typesenseCollection || !d.typesenseSearchKey) {
      var s = root.querySelector('[data-recipes-summary]'); if (s) s.textContent = 'Set host, collection and key in section settings.';
      return;
    }
    root._vg = { cfg: { protocol: d.typesenseProtocol || 'https', host: d.typesenseHost, port: d.typesensePort || '', path: d.typesensePath || '',
      collection: d.typesenseCollection, key: d.typesenseSearchKey, queryBy: d.queryBy || '' },
      perPage: Number(d.perPage || 12), seq: 0, totalPages: 1 };
    var st = root._vg;
    readUrl(st);
    injectCss();
    buildPanel(root);
    buildTitleJump(root);
    setupTiles(root);

    var input = root.querySelector('[data-recipes-search-input]');
    if (input) { input.value = st.query; input.addEventListener('input', debounce(function () { st.query = input.value || ''; go(root, true); }, 250)); }
    var sort = root.querySelector('[data-recipes-sort]');
    if (sort) {
      sort.innerHTML = SORTS.map(function (o) { return '<option value="' + esc(o[0]) + '"' + (st.sortBy === o[0] ? ' selected' : '') + '>' + esc(o[1]) + '</option>'; }).join('');
      sort.addEventListener('change', function () { st.sortBy = sort.value; go(root, true); });
    }
    var oldTime = root.querySelector('[data-recipes-time-filter]'); if (oldTime) { var w = oldTime.closest('.recipes-search__facet'); if (w) w.remove(); }

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-vgrs-input]')) {
        var f = t.getAttribute('data-field'), set = st.selected[f] || (st.selected[f] = new Set());
        if (t.checked) set.add(t.value); else set.delete(t.value);
        track(f, t.value, t.checked);
        go(root, true);
      } else if (t.matches('[data-vgrs-select]')) {
        var sk = t.getAttribute('data-vgrs-select');
        st.selects[sk] = t.value; track(sk, t.value || 'any', !!t.value); go(root, true);
      }
    });
    root.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-vgrs-tab]');
      if (tab) {
        var kv = tab.getAttribute('data-vgrs-tab');
        if (kv) st.selected.kind = new Set([kv]); else delete st.selected.kind;
        track('kind', kv || 'All', !!kv);
        go(root, true); return;
      }
      var more = e.target.closest('[data-vgrs-more]');
      if (more) { var box = root.querySelector('[data-vgrs-facet="' + more.getAttribute('data-vgrs-more') + '"]'); box.setAttribute('data-expanded', box.getAttribute('data-expanded') === '1' ? '0' : '1'); runSearch(root); return; }
      var rm = e.target.closest('[data-vgrs-remove]');
      if (rm) {
        var f = rm.getAttribute('data-vgrs-remove'), v = rm.getAttribute('data-value');
        if (rm.hasAttribute('data-select')) { st.selects[f] = ''; var sel = root.querySelector('[data-vgrs-select="' + f + '"]'); if (sel) sel.value = ''; }
        else if (st.selected[f]) st.selected[f].delete(v);
        track(f, v, false);
        go(root, true); return;
      }
      if (e.target.closest('[data-vgrs-clear]') || e.target.closest('[data-recipes-clear]')) {
        st.selected = {}; st.selects = { time: '', ready: '' }; st.query = ''; st.sortBy = '';
        if (input) input.value = ''; if (sort) sort.value = '';
        root.querySelectorAll('[data-vgrs-select]').forEach(function (s) { s.value = ''; });
        go(root, true); return;
      }
      if (e.target.closest('[data-recipes-prev]') && st.page > 1) { st.page -= 1; runSearch(root); scrollTop(root); }
      else if (e.target.closest('[data-recipes-next]') && st.page < st.totalPages) { st.page += 1; runSearch(root); scrollTop(root); }
    });
    runSearch(root);
  }
  // ------------------------------------------------------------------ quick-pick tiles
  function paramsOf(href) {
    try { return new URL(href, window.location.origin); } catch (e) { return null; }
  }
  function filterFromParams(u) {
    var parts = [];
    FACET_FIELDS.forEach(function (f) {
      var vals = u.searchParams.getAll(f); if (!vals.length) return;
      var def = FACETS.filter(function (x) { return x.field === f; })[0] || {};
      if (def.match === 'all') vals.forEach(function (v) { parts.push(f + ':=' + tick(v)); });
      else parts.push(f + ':=[' + vals.map(tick).join(',') + ']');
    });
    return parts.join(' && ');
  }
  function setupTiles(root) {
    var st = root._vg, wrap = root.querySelector('[data-vgrs-tiles]'); if (!wrap) return;
    var season = wrap.querySelector('[data-vgrs-season]');
    if (season) {
      var m = new Date().getMonth(), s = SEASONS.filter(function (x) { return x.months.indexOf(m) >= 0; })[0];
      if (s) {
        season.setAttribute('href', window.location.pathname + '?' + s.field + '=' + encodeURIComponent(s.value));
        var lab = season.querySelector('[data-vgrs-season-label]'); if (lab) lab.textContent = s.label;
      } else season.remove();
    }
    var tiles = Array.prototype.slice.call(wrap.querySelectorAll('.vgrs-tile'));
    if (!tiles.length) return;
    var searches = tiles.map(function (t) {
      var u = paramsOf(t.getAttribute('href')), f = u ? filterFromParams(u) : '';
      var q = { collection: st.cfg.collection, q: '*', query_by: 'title', per_page: t.hasAttribute('data-vgrs-tile-auto') ? 1 : 0,
        include_fields: 'image_url,image_alt', sort_by: 'published_at:desc' };
      if (f) q.filter_by = f;
      return q;
    });
    fetch(endpoint(st.cfg, '/multi_search'), { method: 'POST',
      headers: { 'X-TYPESENSE-API-KEY': st.cfg.key, 'Content-Type': 'application/json' },
      body: JSON.stringify({ searches: searches }) })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        (p.results || []).forEach(function (res, i) {
          var t = tiles[i], found = (res && !res.error) ? Number(res.found || 0) : -1;
          if (found === 0 && t.hasAttribute('data-vgrs-season')) { t.remove(); return; }
          if (found > 0) {
            var c = t.querySelector('[data-vgrs-count-for]'); if (c) c.textContent = found.toLocaleString() + ' recipes';
            var hit = res.hits && res.hits[0] && res.hits[0].document;
            var media = t.querySelector('.vgrs-tile__media');
            if (hit && hit.image_url && media && !media.querySelector('img')) {
              media.innerHTML = '<img class="vgrs-tile__img" src="' + esc(hit.image_url) + (hit.image_url.indexOf('?') < 0 ? '?width=320' : '&width=320') +
                '" alt="" loading="lazy" width="160" height="160">';
            }
          }
          if (found !== 0) t.hidden = false;
        });
      }).catch(function () { if (season) season.remove(); });
    // a tile that points at this page filters in place instead of reloading
    wrap.addEventListener('click', function (e) {
      var t = e.target.closest('.vgrs-tile'); if (!t || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var u = paramsOf(t.getAttribute('href')); if (!u || u.pathname !== window.location.pathname) return;
      e.preventDefault();
      window.history.replaceState(null, '', u.pathname + u.search);
      readUrl(st);
      var input = root.querySelector('[data-recipes-search-input]'); if (input) input.value = st.query;
      root.querySelectorAll('[data-vgrs-select]').forEach(function (s) { s.value = st.selects[s.getAttribute('data-vgrs-select')] || ''; });
      tiles.forEach(function (x) { x.classList.toggle('is-active', x === t); });
      go(root, true);
      var res = root.querySelector('.recipes-search__results-wrap');
      if (res) res.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function scrollTop(root) { var r = root.getBoundingClientRect(); if (r.top < 0) root.scrollIntoView({ behavior: 'smooth', block: 'start' }); }

  function boot() { document.querySelectorAll('[data-recipes-search-root]').forEach(function (r) { if (!r._vg) init(r); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();